import {
  ActionIcon,
  Box,
  Button,
  Loader,
  SegmentedControl,
  Text,
  Tooltip,
} from "@mantine/core";
import { Calendar } from "@mantine/dates";
import {
  IconChevronLeft,
  IconChevronRight,
  IconHelpCircle,
  IconPencil,
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

import { LEGEND_ITEMS } from "./DSSCalendar.constants";
import { getEventsForDate, hasEvents } from "./DSSCalendar.utils";
import {
  type DSSCalendarServiceType,
  type DSSCalendarView,
} from "./DSSCalendar.types";
import { useDSSCalendarEvents } from "./useDSSCalendarEvents";
import styles from "./DSSCalendar.module.scss";

const serviceDotClass: Record<DSSCalendarServiceType, string> = {
  seaService: styles.dotSeaService,
  watchkeeping: styles.dotWatchkeeping,
  standby: styles.dotStandby,
  yard: styles.dotYard,
  portStay: styles.dotPortStay,
};

export const DSSCalendar = () => {
  const [displayDate, setDisplayDate] = useState(() =>
    dayjs().format("YYYY-MM-DD"),
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [view, setView] = useState<DSSCalendarView>("month");

  const month = dayjs(displayDate).format("YYYY-MM");
  const { events, isLoading } = useDSSCalendarEvents(month);

  const headerLabel = useMemo(
    () =>
      view === "month"
        ? dayjs(displayDate).format("MMMM YYYY").toUpperCase()
        : dayjs(displayDate).format("YYYY"),
    [displayDate, view],
  );

  const navigate = useCallback(
    (direction: -1 | 1) => {
      const unit = view === "month" ? "month" : "year";
      setDisplayDate(
        dayjs(displayDate).add(direction, unit).format("YYYY-MM-DD"),
      );
    },
    [displayDate, view],
  );

  const goToToday = useCallback(() => {
    setDisplayDate(dayjs().format("YYYY-MM-DD"));
    setSelectedDate(dayjs().format("YYYY-MM-DD"));
  }, []);

  const renderDay = useCallback(
    (date: string) => {
      const dayEvents = getEventsForDate(events, date);

      return (
        <div className={styles.dayContent}>
          <span className={styles.dayNumber}>{dayjs(date).date()}</span>
          {dayEvents.length > 0 && (
            <div className={styles.eventDots}>
              {dayEvents.slice(0, 3).map(event => (
                <span
                  key={event.id}
                  className={`${styles.eventDot} ${serviceDotClass[event.type]}`}
                />
              ))}
            </div>
          )}
        </div>
      );
    },
    [events],
  );

  const getDayProps = useCallback(
    (date: string) => {
      const dayHasEvents = hasEvents(events, date);
      const isSelected = selectedDate === date;

      return {
        className: [
          dayHasEvents ? styles.dayWithEvents : "",
          isSelected ? styles.daySelected : "",
        ]
          .filter(Boolean)
          .join(" "),
        "data-has-events": dayHasEvents || undefined,
        onClick: () => setSelectedDate(date),
      };
    },
    [events, selectedDate],
  );

  return (
    <div className={styles.DSSCalendarRoot}>
      {/* <h1 className={styles.pageTitle}>SEA SERVICE</h1> */}

      {isLoading ? (
        <Box className={styles.loader}>
          <Loader />
        </Box>
      ) : (
        <div className={styles.layout}>
          <div className={styles.calendarColumn}>
            <div className={styles.warningBanner}>
              <IconPencil size={14} className={styles.warningIcon} />
              <Text className={styles.warningText}>
                VIEW ONLY – CALENDAR CANNOT BE EDITED. CONTACT YOUR VESSEL ADMIN
                TO UPDATE SEA SERVICE.
              </Text>
            </div>

            <div className={styles.calendarCard}>
              <div className={styles.calendarToolbar}>
                <Text className={styles.monthTitle}>{headerLabel}</Text>

                <div className={styles.toolbarControls}>
                  <SegmentedControl
                    className={styles.viewToggle}
                    value={view}
                    onChange={value => setView(value as DSSCalendarView)}
                    data={[
                      { label: "MONTH", value: "month" },
                      { label: "YEARLY OVERVIEW", value: "year" },
                    ]}
                  />

                  <div className={styles.navGroup}>
                    <ActionIcon
                      variant="default"
                      className={styles.navButton}
                      aria-label="Previous"
                      onClick={() => navigate(-1)}>
                      <IconChevronLeft size={16} />
                    </ActionIcon>
                    <Button
                      variant="default"
                      className={styles.todayButton}
                      onClick={goToToday}>
                      TODAY
                    </Button>
                    <ActionIcon
                      variant="default"
                      className={styles.navButton}
                      aria-label="Next"
                      onClick={() => navigate(1)}>
                      <IconChevronRight size={16} />
                    </ActionIcon>
                  </div>
                </div>
              </div>

              <Text className={styles.subtext}>
                YOU CAN ONLY LOG DAYS WITHIN YOUR SERVICE PERIODS. GAPS BETWEEN
                PERIODS ARE LEAVE AND ARE GREYED OUT.{" "}
                <span className={styles.learnMore}>Learn more</span>
              </Text>

              <Calendar
                date={displayDate}
                onDateChange={value => {
                  if (value) {
                    setDisplayDate(value);
                  }
                }}
                level={view}
                onLevelChange={level => setView(level as DSSCalendarView)}
                maxLevel="year"
                minLevel="month"
                highlightToday
                hideOutsideDates={false}
                classNames={{
                  calendarHeader: styles.calendarHeaderHidden,
                  calendarHeaderControl: styles.calendarHeaderControl,
                  calendarHeaderLevel: styles.calendarHeaderLevel,
                  weekdaysRow: styles.weekdaysRow,
                  weekday: styles.weekday,
                  day: styles.day,
                  month: styles.month,
                  monthCell: styles.monthCell,
                  monthsList: styles.monthsList,
                  monthsListCell: styles.monthsListCell,
                  monthsListRow: styles.monthsListRow,
                  monthsListControl: styles.monthsListControl,
                }}
                getDayProps={getDayProps}
                renderDay={renderDay}
              />
            </div>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.selectionPanel}>
              <div className={styles.selectionHeader}>
                <Text className={styles.selectionTitle}>SELECTION</Text>
                <Tooltip
                  label="Legend for sea service day types"
                  withArrow
                  position="left">
                  <IconHelpCircle size={16} className={styles.selectionHelp} />
                </Tooltip>
              </div>

              <ul className={styles.legendList}>
                {LEGEND_ITEMS.map(item => (
                  <li key={item.type} className={styles.legendItem}>
                    <span
                      className={`${styles.legendDot} ${serviceDotClass[item.type]}`}
                    />
                    <Text className={styles.legendLabel}>{item.label}</Text>
                  </li>
                ))}
              </ul>

              <Text className={styles.sourceLegend}>
                <strong>SOURCE:</strong> Green check = DSS verified. Amber
                warning = needs review. Pencil = manual entry. Pending =
                awaiting approval.
              </Text>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
};
